import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, RefreshCcw, Filter, Truck, Calendar, Layers, Package, X } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { fileUrl } from "@/lib/uploads";
import { MATERIAL_CATEGORIES, matLabel } from "./FactoryOrdersPage";

const iso = (d) => d.toISOString().slice(0, 10);
const nDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const fmtDT = (v) => v ? new Date(v).toLocaleString() : "—";

export default function DispatchReportPage() {
  const [from, setFrom] = useState(nDaysAgo(30));
  const [to, setTo] = useState(iso(new Date()));
  const [projectId, setProjectId] = useState("all");
  const [projectName, setProjectName] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [projects, setProjects] = useState([]);
  const [viewMode, setViewMode] = useState("line_items"); // "line_items" | "truck_loads"
  const [openLoad, setOpenLoad] = useState(null); // drilldown drawer state

  // Load list of projects that have dispatches (for the dropdown)
  useEffect(() => {
    api.get("/factory/dispatches/projects")
      .then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]));
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (projectId && projectId !== "all") params.set("project_id", projectId);
      if (projectName.trim() && projectId === "all") params.set("project_name", projectName.trim());
      if (category && category !== "all") params.set("material_category", category);
      if (status && status !== "all") params.set("status", status);
      const { data } = await api.get(`/factory/dispatches/report?${params.toString()}`);
      setReport(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  }, [from, to, projectId, projectName, category, status]);

  useEffect(() => { run(); }, []); // eslint-disable-line

  const rows = report?.rows || [];
  const summary = report?.summary || {};
  const projectMeta = report?.project_meta;
  const isProjectScoped = projectId !== "all" && !!projectMeta;

  const filenameBase = useMemo(() => {
    if (isProjectScoped) {
      const slug = (projectMeta.project_name || "project").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      return `dispatch_report_${slug}_${from}_${to}`;
    }
    return `dispatch_report_${from}_${to}`;
  }, [isProjectScoped, projectMeta, from, to]);

  const buildTableData = () => rows.map((r) => {
    const files = (r.attachment_file_ids || []).map((fid) => `${window.location.origin}${fileUrl(fid)}`).join(" | ");
    const mat = r.material_category === "other" ? r.material_other : matLabel(r.material_category);
    return {
      "Project": r.project_name || "",
      "Client": r.client_name || "",
      "City": r.city || "",
      "Material Category": mat,
      "Description / Notes": r.description || "",
      "Driver": r.driver_name || "",
      "Vehicle": r.vehicle_number || "",
      "Dispatched At": fmtDT(r.dispatched_at),
      "Dispatched By": r.dispatched_by_name || "",
      "Role": r.dispatched_by_role || "",
      "Status": r.status || "",
      "Files": files,
    };
  });

  const download = (kind) => {
    if (!rows.length) { toast.error("No data to export"); return; }

    if (kind === "csv") {
      const data = buildTableData();
      const headers = Object.keys(data[0]);
      const lines = [headers.join(",")];
      data.forEach((row) => {
        lines.push(headers.map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${filenameBase}.csv`; a.click();
      return;
    }

    if (kind === "xlsx") {
      const data = buildTableData();
      const wb = XLSX.utils.book_new();

      // Sheet 1 — Summary
      const summaryRows = [
        ["Dispatch Report"],
        [],
        ["Period", `${from} → ${to}`],
        ...(isProjectScoped ? [
          ["Project", projectMeta.project_name],
          ["Client · City", `${projectMeta.client_name || "—"}${projectMeta.city ? ` · ${projectMeta.city}` : ""}`],
        ] : []),
        [],
        ["Total dispatches", summary.total_dispatches ?? 0],
        ["Total projects covered", summary.total_projects ?? 0],
        ["Total material categories", summary.total_categories ?? 0],
        ["First dispatch date", fmtDT(summary.first_dispatch_at)],
        ["Latest dispatch date", fmtDT(summary.latest_dispatch_at)],
        [],
        ["Category breakdown"],
        ...Object.entries(summary.by_category || {}).map(([k, v]) => [matLabel(k) || k, v]),
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
      ws1["!cols"] = [{ wch: 28 }, { wch: 42 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Summary");

      // Sheet 2 — Line items
      const ws2 = XLSX.utils.json_to_sheet(data);
      ws2["!cols"] = Object.keys(data[0]).map((h) => ({ wch: Math.max(12, h.length + 2) }));
      XLSX.utils.book_append_sheet(wb, ws2, "Dispatches");

      XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      return;
    }

    // PDF
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("Dispatch Report", 40, 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);

    let y = 58;
    if (isProjectScoped) {
      doc.setFont("helvetica", "bold");
      doc.text(`${projectMeta.project_name}`, 40, y);
      doc.setFont("helvetica", "normal");
      doc.text(`${projectMeta.client_name || ""}${projectMeta.city ? ` · ${projectMeta.city}` : ""}`, 40, y + 12);
      y += 26;
    }
    const summaryLine =
      `Period: ${from} → ${to}  ·  Dispatches: ${summary.total_dispatches}` +
      `  ·  Projects: ${summary.total_projects}  ·  Categories: ${summary.total_categories}`;
    doc.text(summaryLine, 40, y);
    doc.text(
      `First: ${fmtDT(summary.first_dispatch_at)}   Latest: ${fmtDT(summary.latest_dispatch_at)}`,
      40, y + 12
    );

    autoTable(doc, {
      startY: y + 26,
      head: [["Project", "Client · City", "Material", "Description", "Driver", "Vehicle", "When", "By (Role)", "Files"]],
      body: rows.map((r) => [
        r.project_name,
        `${r.client_name}${r.city ? ` · ${r.city}` : ""}`,
        r.material_category === "other" ? r.material_other : matLabel(r.material_category),
        r.description || "",
        r.driver_name, r.vehicle_number,
        fmtDT(r.dispatched_at),
        `${r.dispatched_by_name}${r.dispatched_by_role ? ` (${r.dispatched_by_role})` : ""}`,
        (r.attachment_file_ids || []).length ? `${(r.attachment_file_ids || []).length} file(s)` : "—",
      ]),
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
      columnStyles: { 3: { cellWidth: 120 } },
    });
    doc.save(`${filenameBase}.pdf`);
  };

  const selectedProject = projects.find((p) => p.project_id === projectId);

  return (
    <div className="p-4 sm:p-10 space-y-6" data-testid="dispatch-report-page">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Factory · Reports</p>
        <h1 className="font-display text-3xl text-white mt-1">Dispatch Report</h1>
        <p className="text-xs text-gray-500 mt-1">
          Filter by a specific project to see every dispatch across Production, Procurement,
          Purchase and Executive users — with export to PDF or Excel.
        </p>
      </div>

      <div className="iz-card p-5" data-testid="dispatch-report-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#d4af37]"/>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Filters</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">From</p>
            <input type="date" className="iz-input w-full" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="dr-from"/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">To</p>
            <input type="date" className="iz-input w-full" value={to} onChange={(e) => setTo(e.target.value)} data-testid="dr-to"/>
          </div>
          <div className="lg:col-span-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Project (Project-Wise Report)</p>
            <select className="iz-input w-full" value={projectId}
                    onChange={(e) => { setProjectId(e.target.value); if (e.target.value !== "all") setProjectName(""); }}
                    data-testid="dr-project-select">
              <option value="all">All projects</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_name}{p.client_name ? ` — ${p.client_name}` : ""} ({p.dispatches_count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Material</p>
            <select className="iz-input w-full" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="dr-category">
              <option value="all">All</option>
              {MATERIAL_CATEGORIES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Status</p>
            <select className="iz-input w-full" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dr-status">
              <option value="all">All</option>
              <option value="dispatched">Dispatched</option>
            </select>
          </div>
          {projectId === "all" && (
            <div className="lg:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Project Name search (used only when no project selected above)</p>
              <input className="iz-input w-full" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. Assetz Marq" data-testid="dr-project-name"/>
            </div>
          )}
          <div className="flex items-end lg:col-span-6">
            <button onClick={run} disabled={loading} className="iz-btn-primary text-xs flex items-center gap-2"
                    data-testid="dr-generate">
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
              {loading ? "Loading…" : "Generate Report"}
            </button>
          </div>
        </div>
      </div>

      {report && (
        <>
          {/* Project scoped header */}
          {isProjectScoped && (
            <div className="iz-card p-5 border-[#d4af37]/40" data-testid="dr-project-header">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Project</p>
              <h2 className="font-display text-2xl text-white mt-1">{projectMeta.project_name}</h2>
              <p className="text-sm text-gray-400 mt-1">
                {projectMeta.client_name}{projectMeta.city ? ` · ${projectMeta.city}` : ""}
              </p>
            </div>
          )}

          {/* Summary grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dr-summary">
            <SummaryCard label="Total Dispatches" value={summary.total_dispatches ?? 0} tone="white"/>
            <SummaryCard label={isProjectScoped ? "Project" : "Projects Covered"}
                         value={isProjectScoped ? 1 : (summary.total_projects ?? 0)} tone="gold"/>
            <SummaryCard label="Material Categories" value={summary.total_categories ?? 0} tone="gold"
                         icon={<Layers className="w-3 h-3"/>}/>
            <SummaryCard label="First Dispatch" value={fmtDT(summary.first_dispatch_at)} tone="soft" small
                         icon={<Calendar className="w-3 h-3"/>}/>
            <SummaryCard label="Latest Dispatch" value={fmtDT(summary.latest_dispatch_at)} tone="soft" small
                         icon={<Calendar className="w-3 h-3"/>}/>
            <div className="iz-card p-4 flex flex-col justify-end gap-2">
              <div className="grid grid-cols-3 gap-1.5">
                <button onClick={() => download("csv")} className="iz-btn-secondary text-[11px] flex items-center justify-center gap-1" data-testid="dr-download-csv">
                  <FileSpreadsheet className="w-3 h-3"/> CSV
                </button>
                <button onClick={() => download("xlsx")} className="iz-btn-secondary text-[11px] flex items-center justify-center gap-1" data-testid="dr-download-xlsx">
                  <FileSpreadsheet className="w-3 h-3"/> Excel
                </button>
                <button onClick={() => download("pdf")} className="iz-btn-primary text-[11px] flex items-center justify-center gap-1" data-testid="dr-download-pdf">
                  <FileText className="w-3 h-3"/> PDF
                </button>
              </div>
            </div>
          </div>

          {/* Category breakdown when project-scoped */}
          {isProjectScoped && Object.keys(summary.by_category || {}).length > 0 && (
            <div className="iz-card p-5" data-testid="dr-category-breakdown">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-[#d4af37]"/>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Category Breakdown</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.by_category).map(([k, v]) => (
                  <span key={k} className="px-3 py-1.5 rounded-md border border-[#262626] bg-[#0a0a0a] text-xs">
                    <span className="text-white">{matLabel(k) || k}</span>
                    <span className="text-[#d4af37] font-mono ml-2">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* View mode tabs */}
          <div className="iz-card p-2 flex flex-wrap gap-1" data-testid="dr-view-tabs">
            <button onClick={() => setViewMode("line_items")}
                    className={`px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                      viewMode === "line_items"
                        ? "bg-[#d4af37] text-black"
                        : "text-gray-400 hover:text-white hover:bg-[#111]"
                    }`}
                    data-testid="dr-tab-line-items">
              <Truck className="w-3.5 h-3.5"/> Line Items ({rows.length})
            </button>
            <button onClick={() => setViewMode("truck_loads")}
                    className={`px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                      viewMode === "truck_loads"
                        ? "bg-[#d4af37] text-black"
                        : "text-gray-400 hover:text-white hover:bg-[#111]"
                    }`}
                    data-testid="dr-tab-truck-loads">
              <Package className="w-3.5 h-3.5"/> By Truck Load ({(report?.truck_loads || []).length})
            </button>
          </div>

          {viewMode === "truck_loads" ? (
            <div className="iz-card p-5" data-testid="dr-truck-loads-table">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-[#d4af37]"/>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                  Truck Loads ({(report?.truck_loads || []).length}) — grouped by bulk dispatch
                </p>
                <span className="ml-auto text-[10px] text-gray-500 italic">click a row to drill in</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[880px]">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                    <tr>
                      {!isProjectScoped && <th className="text-left py-2 px-2">Project · Client</th>}
                      <th className="text-left py-2 px-2">Categories in Load</th>
                      <th className="text-left py-2 px-2">Driver · Vehicle</th>
                      <th className="text-left py-2 px-2">Dispatched At</th>
                      <th className="text-left py-2 px-2">By (Role)</th>
                      <th className="text-left py-2 px-2">Items</th>
                      <th className="text-left py-2 px-2">Files</th>
                      <th className="text-left py-2 px-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.truck_loads || []).map((load) => (
                      <tr key={load.dispatch_ids?.[0] || load.bulk_group_id}
                          className="border-b border-[#161616] hover:bg-[#111] cursor-pointer transition-colors"
                          onClick={() => setOpenLoad(load)}
                          data-testid={`dr-truck-row-${load.bulk_group_id || load.dispatch_ids?.[0]}`}>
                        {!isProjectScoped && (
                          <td className="py-2 px-2">
                            <p className="text-white">{load.client_name}{load.city ? ` · ${load.city}` : ""}</p>
                            <p className="text-[10px] text-gray-500">{load.project_name}</p>
                          </td>
                        )}
                        <td className="py-2 px-2 text-gray-300">
                          <div className="flex flex-wrap gap-1">
                            {(load.categories || []).map((c) => (
                              <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#262626] bg-[#0a0a0a] text-white">
                                {c}
                              </span>
                            ))}
                          </div>
                          {(load.descriptions || []).length > 0 && (
                            <p className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap">
                              {load.descriptions.join(" · ")}
                            </p>
                          )}
                        </td>
                        <td className="py-2 px-2 text-gray-300">
                          <p>{load.driver_name || <span className="text-gray-600">—</span>}</p>
                          <p className="text-[10px] text-gray-500 font-mono">{load.vehicle_number || ""}</p>
                        </td>
                        <td className="py-2 px-2 text-gray-500 font-mono">{fmtDT(load.dispatched_at)}</td>
                        <td className="py-2 px-2 text-gray-300">
                          <p>{load.dispatched_by_name}</p>
                          {load.dispatched_by_role && <p className="text-[10px] text-[#d4af37] font-mono uppercase tracking-widest">{load.dispatched_by_role}</p>}
                        </td>
                        <td className="py-2 px-2 text-white font-mono">{(load.dispatch_ids || []).length}</td>
                        <td className="py-2 px-2 text-[#d4af37] font-mono">{load.attachment_count || 0}</td>
                        <td className="py-2 px-2">
                          {load.is_solo ? (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Solo</span>
                          ) : (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">Bulk</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(report?.truck_loads || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8 italic text-xs">No truck loads for these filters.</p>
                )}
              </div>
            </div>
          ) : (
          <div className="iz-card p-5" data-testid="dr-table">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-4 h-4 text-[#d4af37]"/>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatch Line Items ({rows.length})</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[880px]">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                  <tr>
                    {!isProjectScoped && <th className="text-left py-2 px-2">Project</th>}
                    {!isProjectScoped && <th className="text-left py-2 px-2">Client · City</th>}
                    <th className="text-left py-2 px-2">Material · Notes</th>
                    <th className="text-left py-2 px-2">Driver · Vehicle</th>
                    <th className="text-left py-2 px-2">When</th>
                    <th className="text-left py-2 px-2">By (Role)</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Files</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-[#161616] hover:bg-[#0f0f0f]" data-testid={`dr-row-${r.id}`}>
                      {!isProjectScoped && <td className="py-2 px-2 text-white">{r.project_name}</td>}
                      {!isProjectScoped && <td className="py-2 px-2 text-gray-300">{r.client_name}{r.city ? ` · ${r.city}` : ""}</td>}
                      <td className="py-2 px-2 text-gray-300">
                        <span className="text-white">
                          {r.material_category === "other" ? r.material_other : matLabel(r.material_category)}
                        </span>
                        {r.description && <p className="text-[10px] text-gray-500 mt-0.5 whitespace-pre-wrap">{r.description}</p>}
                      </td>
                      <td className="py-2 px-2 text-gray-300">
                        <p>{r.driver_name}</p>
                        <p className="text-[10px] text-gray-500 font-mono">{r.vehicle_number}</p>
                      </td>
                      <td className="py-2 px-2 text-gray-500 font-mono">{fmtDT(r.dispatched_at)}</td>
                      <td className="py-2 px-2 text-gray-300">
                        <p>{r.dispatched_by_name}</p>
                        {r.dispatched_by_role && <p className="text-[10px] text-[#d4af37] font-mono uppercase tracking-widest">{r.dispatched_by_role}</p>}
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">{r.status || "—"}</span>
                      </td>
                      <td className="py-2 px-2">
                        {(r.attachment_file_ids || []).length > 0 ? (
                          <span className="text-[10px] text-[#d4af37]">
                            {(r.attachment_file_ids || []).map((fid, i) => (
                              <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="hover:underline mr-2">
                                file {i + 1}
                              </a>
                            ))}
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <p className="text-center text-gray-500 py-8 italic text-xs">No dispatches for these filters.</p>
              )}
            </div>
          </div>
          )}
        </>
      )}

      {openLoad && (
        <TruckLoadDrawer
          load={openLoad}
          rows={rows}
          isProjectScoped={isProjectScoped}
          onClose={() => setOpenLoad(null)}
        />
      )}
    </div>
  );
}

function TruckLoadDrawer({ load, rows, isProjectScoped, onClose }) {
  // Line items that belong to this bulk_group_id (or the solo dispatch_id).
  const items = useMemo(() => {
    const ids = new Set(load.dispatch_ids || []);
    return rows.filter((r) => ids.has(r.id));
  }, [load, rows]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-stretch justify-end"
         onClick={onClose} data-testid="truck-load-drawer">
      <div className="bg-[#0a0a0a] border-l border-[#262626] w-full max-w-xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0a] px-5 py-4 border-b border-[#262626] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                {load.is_solo ? "Solo Dispatch" : "Truck Load"}
              </p>
              {!load.is_solo && (
                <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37]">
                  BULK · {items.length} items
                </span>
              )}
            </div>
            {!isProjectScoped && (
              <>
                <h3 className="font-display text-xl text-white">{load.client_name}{load.city ? ` · ${load.city}` : ""}</h3>
                <p className="text-[11px] text-gray-500">{load.project_name}</p>
              </>
            )}
            <p className="text-[11px] text-gray-500 font-mono mt-1">{fmtDT(load.dispatched_at)}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1"
                  data-testid="truck-load-drawer-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {/* Meta */}
        <div className="px-5 py-4 border-b border-[#161616] space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Driver</p>
              <p className="text-white">{load.driver_name || <span className="text-gray-600">—</span>}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Vehicle</p>
              <p className="text-white font-mono">{load.vehicle_number || <span className="text-gray-600">—</span>}</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Dispatched By</p>
              <p className="text-white">{load.dispatched_by_name}</p>
              {load.dispatched_by_role && <p className="text-[10px] text-[#d4af37] font-mono uppercase tracking-widest">{load.dispatched_by_role}</p>}
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Files Attached</p>
              <p className="text-white font-mono">{load.attachment_count}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="px-5 py-4 space-y-3" data-testid="truck-load-drawer-items">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            Materials In This Load ({items.length})
          </p>
          {items.map((r, idx) => {
            const mat = r.material_category === "other" ? r.material_other : matLabel(r.material_category);
            return (
              <div key={r.id} className="border border-[#1c1c1c] bg-[#080808] rounded-md p-3 space-y-2"
                   data-testid={`truck-load-drawer-item-${idx}`}>
                <div className="flex items-center justify-between">
                  <p className="text-white text-sm">{mat}</p>
                  <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded px-1.5 py-0.5">
                    {r.status || "dispatched"}
                  </span>
                </div>
                {r.description && (
                  <p className="text-[11px] text-gray-400 whitespace-pre-wrap">{r.description}</p>
                )}
                {(r.attachment_file_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(r.attachment_file_ids || []).map((fid, i) => (
                      <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                         className="text-[10px] text-[#d4af37] hover:underline font-mono">
                        📎 file {i + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && (
            <p className="text-center text-gray-500 py-6 italic text-xs">No items resolved for this load.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone = "white", small = false, icon = null }) {
  const valColor = tone === "gold" ? "text-[#d4af37]" : tone === "soft" ? "text-gray-300" : "text-white";
  return (
    <div className="iz-card p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
        {icon}{label}
      </p>
      <p className={`font-display ${small ? "text-sm" : "text-2xl"} ${valColor} mt-1`}>{value}</p>
    </div>
  );
}
